// src/utils/unsplash.ts
import { createApi } from 'unsplash-js';

export const unsplash = createApi({
  accessKey: process.env.REACT_APP_UNSPLASH_ACCESS_KEY || 'YOUR_UNSPLASH_ACCESS_KEY',
  cache: 'force-cache',
  headers: { 'Accept-Version': 'v1' }
});

// Adjusted interface to match Unsplash API types
export interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string | null; // Allow null
  user: {
    name: string;
    links: {
      html: string;
    };
  };
}

/**
 * Fetches a random image from Unsplash based on query parameters
 * @param query Search terms to find images
 * @param count Number of images to return (default: 1)
 * @returns Promise with array of image objects
 */
export async function getRandomUnsplashImages(query: string, count: number = 1): Promise<UnsplashImage[]> {
  try {
    console.log(`Fetching Unsplash images for query: "${query}" (count: ${count})`);
    
    const result = await unsplash.photos.getRandom({
      query,
      count,
      orientation: 'landscape'
    });

    if (result.errors) {
      console.error('Unsplash API Error:', result.errors[0]);
      return [];
    }

    // Handle the response format (array or single object)
    const photos = Array.isArray(result.response) 
      ? result.response 
      : [result.response];
    
    console.log(`Successfully fetched ${photos.length} images from Unsplash for query "${query}"`);
    return photos as UnsplashImage[];
  } catch (error) {
    console.error('Error fetching images from Unsplash:', error);
    return [];
  }
}

/**
 * Fetches a unique Unsplash image based on query parameters
 * @param query Search terms to find image
 * @returns Promise with image object or null
 */
export const fetchUniqueUnsplashImage = async (query: string): Promise<UnsplashImage | null> => {
  try {
    console.log(`Fetching unique Unsplash image for query: "${query}"`);
    const result = await unsplash.photos.getRandom({
      query,
      count: 1,
      orientation: 'landscape'
    });

    if (result.errors) {
      console.error('Unsplash API Error:', result.errors[0]);
      return null;
    }

    const photo = Array.isArray(result.response) ? result.response[0] : result.response;
    console.log(`Successfully fetched unique image from Unsplash for query "${query}"`);
    return photo as UnsplashImage;
  } catch (error) {
    console.error('Unsplash Fetch Error:', error);
    return null;
  }
};
